import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import ProtectedRoute from '@/components/ProtectedRoute';
import { CoachAcademyLayout } from '@/components/layout/CoachAcademyLayout';
import PublicOrgPage from '@/pages/public/PublicOrgPage';
import OrgLessonsRoute from '@/pages/public/OrgLessonsRoute';
import OrgLoginModalRedirect from '@/pages/public/OrgLoginModalRedirect';
import AcademyForgotPasswordPage from '@/pages/public/AcademyForgotPasswordPage';
import AcademyResetPasswordPage from '@/pages/public/AcademyResetPasswordPage';
import OrgRegisterPage from '@/pages/public/OrgRegisterPage';
import OrgEmailVerificationPage from '@/pages/public/OrgEmailVerificationPage';
import { PublicBookingIndexRedirect } from '@/pages/public/publicBooking/PublicBookingIndexRedirect';
import { BookingAuthStep } from '@/pages/public/publicBooking/steps/BookingAuthStep';
import { BookingSessionsStep } from '@/pages/public/publicBooking/steps/BookingSessionsStep';
import { BookingRegisterStep } from '@/pages/public/publicBooking/steps/BookingRegisterStep';
import { BookingDetailsStep } from '@/pages/public/publicBooking/steps/BookingDetailsStep';
import { BookingPaymentStep } from '@/pages/public/publicBooking/steps/BookingPaymentStep';
import { BookingSuccessStep } from '@/pages/public/publicBooking/steps/BookingSuccessStep';
import { BookingEOIStep } from '@/pages/public/publicBooking/steps/BookingEOIStep';
import { LegacyCampGroupSubdomainRedirect } from '@/components/LegacyCampGroupRedirect';
import { LegacyOrgPrefixRedirect } from '@/components/LegacyOrgRedirects';
import { LegacyAcademyToOrgRedirect } from '@/components/LegacyAcademyToOrgRedirect';
import { LegacyClubToOrgRedirect } from '@/components/LegacyClubToOrgRedirect';
import { memberPortalChildRoutes } from '@/routes/memberPortalChildRoutes';
import { PublicOrgRouteShell } from '@/components/public/PublicOrgRouteShell';
import { PublicOrgMemberGate } from '@/components/layout/PublicOrgMemberGate';
import CoachDashboard from '@/pages/dashboards/coach/CoachDashboard';
import CoachSchedulePage from '@/pages/dashboards/coach/CoachSchedulePage';
import CoachLessonsPage from '@/pages/dashboards/coach/CoachLessonsPage';
import CoachRosterPage from '@/pages/dashboards/coach/CoachRosterPage';
import CoachStudentsPage from '@/pages/dashboards/coach/CoachStudentsPage';
import CoachStudentDetailPage from '@/pages/dashboards/coach/CoachStudentDetailPage';
import CoachProfilePage from '@/pages/dashboards/coach/CoachProfilePage';
import CoachAvailabilityPage from '@/pages/dashboards/coach/CoachAvailabilityPage';
import CoachAttendancePage from '@/pages/dashboards/coach/CoachAttendancePage';
import Settings from '@/pages/shared/Settings';
import Notifications from '@/pages/shared/Notifications';

const OrgPublicBookingRoute = lazy(() => import('@/pages/public/OrgPublicBookingRoute'));
const PublicPrivateBookingPage = lazy(() => import('@/pages/public/privateBooking/PublicPrivateBookingPage'));
const PublicClubCourtHirePage = lazy(() => import('@/pages/public/OrgCourtsHubPage'));
const PublicClubJoinPage = lazy(() => import('@/pages/public/PublicClubJoinPage'));
const PublicPlayEventsPage = lazy(() => import('@/pages/public/PublicPlayEventsPage'));
const PublicPlayEventPage = lazy(() => import('@/pages/public/PublicPlayEventPage'));
const PlayEventPublicBoardPage = lazy(() => import('@/pages/public/PlayEventPublicBoardPage'));

const spinner = (
  <div className="flex min-h-[200px] items-center justify-center">
    <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-gray-900 dark:border-white" />
  </div>
);

const bookingChildRoutes = (
  <>
    <Route index element={<PublicBookingIndexRedirect />} />
    <Route path="auth" element={<Navigate to="register" replace />} />
    <Route path="register" element={<BookingAuthStep />} />
    <Route path="sessions" element={<BookingSessionsStep />} />
    <Route path="signup" element={<BookingRegisterStep />} />
    <Route path="eoi" element={<BookingEOIStep />} />
    <Route path="details" element={<BookingDetailsStep />} />
    <Route path="payment" element={<BookingPaymentStep />} />
    <Route path="success" element={<BookingSuccessStep />} />
  </>
);

interface OrgPublicRoutesProps {
  /** When true, paths are subdomain-relative (no /club-academy/:orgSlug prefix). */
  subdomain?: boolean;
}

import { OrgInventoryHiresRedirect } from '@/components/public/org/OrgInventoryHiresRedirect';
import { OrgClubPaymentsRedirect } from '@/components/public/org/OrgClubPaymentsRedirect';
import { OrgHostOutlet } from '@/routes/OrgHostOutlet';

/** Route elements for path-mode app host (embed in App.tsx Routes). */
export function orgPathModeRouteElements() {
  return (
    <>
      <Route path="/academy/:academySlug/*" element={<LegacyAcademyToOrgRedirect />} />
      <Route path="/club/:slug/*" element={<LegacyClubToOrgRedirect />} />

      {/*
        OrgHostOutlet owns :orgSlug so layout providers receive the param (react-router v7
        pathless layouts do not inherit dynamic segments from absolute child paths).
      */}
      <Route path="/club-academy/:orgSlug" element={<OrgHostOutlet />}>
        <Route index element={<PublicOrgPage />} />
        <Route path="lessons/camps/:groupId" element={<LegacyCampGroupSubdomainRedirect />} />
        <Route path="location/:locationId" element={<OrgLessonsRoute />} />
        <Route path="lessons" element={<OrgLessonsRoute />} />
        <Route
          path="book-private"
          element={
            <Suspense fallback={spinner}>
              <PublicPrivateBookingPage />
            </Suspense>
          }
        />
        <Route
          path="book/:lessonId"
          element={
            <Suspense fallback={spinner}>
              <OrgPublicBookingRoute />
            </Suspense>
          }
        >
          {bookingChildRoutes}
        </Route>
        <Route
          path="court-hire/*"
          element={
            <Suspense fallback={spinner}>
              <PublicClubCourtHirePage />
            </Suspense>
          }
        />
        <Route path="inventory-hires" element={<OrgInventoryHiresRedirect />} />
        <Route path="club-payments" element={<OrgClubPaymentsRedirect />} />
        <Route
          path="join"
          element={
            <Suspense fallback={spinner}>
              <PublicClubJoinPage />
            </Suspense>
          }
        />
        <Route
          path="play"
          element={
            <Suspense fallback={spinner}>
              <PublicPlayEventsPage />
            </Suspense>
          }
        />
        <Route
          path="play/:slug/board"
          element={
            <Suspense fallback={spinner}>
              <PlayEventPublicBoardPage />
            </Suspense>
          }
        />
        <Route
          path="play/:slug"
          element={
            <Suspense fallback={spinner}>
              <PublicPlayEventPage />
            </Suspense>
          }
        />
        <Route path="login" element={<OrgLoginModalRedirect />} />
        <Route path="forgot-password" element={<AcademyForgotPasswordPage />} />
        <Route path="reset-password" element={<AcademyResetPasswordPage />} />
        <Route path="register" element={<OrgRegisterPage />} />
        <Route path="email-verification" element={<OrgEmailVerificationPage />} />

        <Route
          element={
            <ProtectedRoute requireRole={['guardian', 'student', 'member']}>
              <PublicOrgMemberGate />
            </ProtectedRoute>
          }
        >
          <Route
            element={
              <PublicOrgRouteShell>
                <Outlet />
              </PublicOrgRouteShell>
            }
          >
            {memberPortalChildRoutes()}
          </Route>
        </Route>
      </Route>
    </>
  );
}

/** Shared public org route tree for path and subdomain hosts. */
export function OrgPublicRoutes({ subdomain = false }: OrgPublicRoutesProps) {
  if (subdomain) {
    return (
      <Routes>
        <Route path="/academy/:academySlug/*" element={<LegacyOrgPrefixRedirect legacyKind="academy" />} />
        <Route path="/club/:slug/*" element={<LegacyOrgPrefixRedirect legacyKind="club" />} />

        <Route path="/" element={<PublicOrgPage />} />
        <Route path="/lessons/camps/:groupId" element={<LegacyCampGroupSubdomainRedirect />} />
        <Route path="/location/:locationId" element={<OrgLessonsRoute />} />
        <Route path="/lessons" element={<OrgLessonsRoute />} />
        <Route
          path="/book-private"
          element={
            <Suspense fallback={spinner}>
              <PublicPrivateBookingPage />
            </Suspense>
          }
        />
        <Route
          path="/book/:lessonId"
          element={
            <Suspense fallback={spinner}>
              <OrgPublicBookingRoute />
            </Suspense>
          }
        >
          {bookingChildRoutes}
        </Route>
        <Route
          path="/court-hire/*"
          element={
            <Suspense fallback={spinner}>
              <PublicClubCourtHirePage />
            </Suspense>
          }
        />
        <Route path="/inventory-hires" element={<OrgInventoryHiresRedirect />} />
        <Route path="/club-payments" element={<OrgClubPaymentsRedirect />} />
        <Route
          path="/join"
          element={
            <Suspense fallback={spinner}>
              <PublicClubJoinPage />
            </Suspense>
          }
        />
        <Route
          path="/play"
          element={
            <Suspense fallback={spinner}>
              <PublicPlayEventsPage />
            </Suspense>
          }
        />
        <Route
          path="/play/:slug/board"
          element={
            <Suspense fallback={spinner}>
              <PlayEventPublicBoardPage />
            </Suspense>
          }
        />
        <Route
          path="/play/:slug"
          element={
            <Suspense fallback={spinner}>
              <PublicPlayEventPage />
            </Suspense>
          }
        />
        <Route path="/login" element={<OrgLoginModalRedirect />} />
        <Route path="/forgot-password" element={<AcademyForgotPasswordPage />} />
        <Route path="/reset-password" element={<AcademyResetPasswordPage />} />
        <Route path="/register" element={<OrgRegisterPage />} />
        <Route path="/email-verification" element={<OrgEmailVerificationPage />} />

        <Route
          element={
            <ProtectedRoute requireRole={['guardian', 'student', 'member']}>
              <PublicOrgMemberGate />
            </ProtectedRoute>
          }
        >
          <Route
            element={
              <PublicOrgRouteShell>
                <Outlet />
              </PublicOrgRouteShell>
            }
          >
            {memberPortalChildRoutes()}
          </Route>
        </Route>

        <Route
          path="/coach"
          element={
            <ProtectedRoute requireRole="coach">
              <CoachAcademyLayout />
            </ProtectedRoute>
          }
        >
          <Route path="dashboard" element={<CoachDashboard />} />
          <Route path="schedule" element={<CoachSchedulePage />} />
          <Route path="availability" element={<CoachAvailabilityPage />} />
          <Route path="lessons" element={<CoachLessonsPage />} />
          <Route path="roster" element={<CoachRosterPage />} />
          <Route path="attendance" element={<CoachAttendancePage />} />
          <Route path="students" element={<CoachStudentsPage />} />
          <Route path="students/:studentId" element={<CoachStudentDetailPage />} />
          <Route path="profile" element={<CoachProfilePage />} />
          <Route path="settings" element={<Settings />} />
          <Route path="notifications" element={<Notifications />} />
        </Route>

        <Route path="*" element={<div className="mt-12 text-center">404 - Page Not Found</div>} />
      </Routes>
    );
  }

  return <Routes>{orgPathModeRouteElements()}</Routes>;
}
